const _ = require("lodash");

// Inspired by https://github.com/vslinko/hoc/blob/master/index.js
// In all higher order components, wrap the component-creating function with
// augmentHOCWrapper. Then if you ever need to access the base component from
// one that might be higher order, use getBaseComponent. And if you ever need
// to access the instance of the base component, use getBaseInstance.

// Internal helper
const isRefable = function(Component) {
	return !!Component.prototype.render;
};

// Internal helper. Combine function ref properties.
const combineRefs = function(...refs) {
	refs = refs.filter(Boolean);
	return passedInstance => refs.forEach(refFunc => refFunc(passedInstance));
};

const wrappedCompKey = "$$_wrappedComp";
const wrappedInstKey = "$$_wrappedInst";

const augmentHOCWrapper = function(makeHOC) {
	return function(BaseComponent, ...args) {
		const WrappingComponent = makeHOC(BaseComponent, ...args);
		WrappingComponent[wrappedCompKey] = BaseComponent;

		const origRender = WrappingComponent.prototype.render;
		if (origRender) {
			// Wrap the render function of the new component to inject a ref, needed
			// to make getBaseInstance work. This is a pretty horrific hack.
			WrappingComponent.prototype.render = function() {
				let baseEl = origRender.call(this);
				if (
					baseEl &&
					baseEl.type === BaseComponent &&
					isRefable(BaseComponent)
				) {
					// React seals/freezes the return of a render call in a dev build, so
					// we have to clone it to mangle it.
					baseEl = _.extend({}, baseEl, {
						ref: combineRefs(baseEl.ref, r => {
							this[wrappedInstKey] = r;
						}),
					});
				} else {
					// Note that getBaseInstance handles null and undefined differently.
					this[wrappedInstKey] = null;
				}
				return baseEl;
			};
		} else if (typeof BaseComponent === "function") {
			// For functional components, just return the wrapping component
			return WrappingComponent;
		}

		return WrappingComponent;
	};
};

const getBaseComponent = function(Component) {
	while (Component[wrappedCompKey]) {
		Component = Component[wrappedCompKey];
	}
	return Component;
};

// This returns null if any of the higher-order components render a top element
// with a class different from the wrapped component, or if the base instance
// is a stateless functional component.
const getBaseInstance = function(instance) {
	while (instance && instance[wrappedInstKey] !== undefined) {
		instance = instance[wrappedInstKey];
	}
	return instance;
};

module.exports = {
	augmentHOCWrapper,
	getBaseComponent,
	getBaseInstance,
};
